export interface ErrorPayloadData {
  error: Error;
}

export interface BusSyncEventData<T> {
  data: {
    type: string;
    payload: T & ErrorPayloadData;
  };
}

export interface BusEvent {
  type: string;
  payload?: unknown;
}

export interface BusSyncEvent {
  event: BusEvent;
  target: HTMLIFrameElement;
  fulfilled: string;
  rejected?: string;
}

export interface BusAsyncEvent<T> {
  event: BusEvent;
  target: HTMLIFrameElement;
  asyncEventName: string;
  shouldResolve: (payload: T) => boolean;
}

export interface BusFireAndForgetEvent {
  event: BusEvent;
  target: HTMLIFrameElement;
}

export class BusEventsPromisifier {
  static promisifySyncEvent<T>(syncEvent: BusSyncEvent): Promise<T> {
    const { target, fulfilled, rejected, event } = syncEvent;

    return new Promise<T>((resolve, reject) => {
      const eventListener = (event: BusSyncEventData<T>) => {
        const {
          data: { type, payload },
        } = event;

        /**
         * window.removeEventListener should be called separately for both fulfilled and rejected.
         * DO NOT MOVE IT OUT of if else condition.
         */

        if (type === fulfilled) {
          window.removeEventListener('message', eventListener);
          resolve(payload);
        } else if (type === rejected) {
          window.removeEventListener('message', eventListener);
          reject(payload?.error);
        }
      };

      window.addEventListener('message', eventListener);
      this.forwardEventToBus(target, event);
    });
  }

  static async promisifyAsyncEvent<T>(
    asyncEvent: BusAsyncEvent<T>,
  ): Promise<T> {
    const { target, event, asyncEventName, shouldResolve } = asyncEvent;

    return new Promise<T>((resolve) => {
      const eventListener = (event: BusSyncEventData<T>) => {
        const {
          data: { type, payload },
        } = event;

        if (type === asyncEventName && shouldResolve(payload)) {
          window.removeEventListener('message', eventListener);
          resolve(payload);
        }
      };

      window.addEventListener('message', eventListener);
      this.forwardEventToBus(target, event);
    });
  }

  static async promisifyFireAndForgetEvent(
    ffEvent: BusFireAndForgetEvent,
  ): Promise<void> {
    const { target, event } = ffEvent;
    this.forwardEventToBus(target, event);
  }

  private static forwardEventToBus(target: HTMLIFrameElement, event: BusEvent) {
    const busUrl = `${process.env.BUS_URL}/${process.env.BUS_VERSION}/`;
    const busOrigin = new URL(busUrl).origin;
    target.contentWindow?.postMessage(event, busOrigin);
  }
}
